import api from './api';

interface ProfilePictureResponse {
  success: boolean;
  message?: string;
  data?: {
    profilePicture: string;
    profilePictureUrl: string;
    businessName?: string;
  };
}

class ProfilePictureService {
  /**
   * Upload profile picture
   */
  async uploadProfilePicture(file: File): Promise<ProfilePictureResponse> {
    const formData = new FormData();
    formData.append('profilePicture', file);

    const response = await api.post<ProfilePictureResponse>(
      '/agency/profile-picture',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  /**
   * Get current profile picture
   */
  async getProfilePicture(): Promise<ProfilePictureResponse> {
    const response = await api.get<ProfilePictureResponse>('/agency/profile-picture');
    return response.data;
  }

  /**
   * Update profile picture (same as upload)
   */
  async updateProfilePicture(file: File): Promise<ProfilePictureResponse> {
    const formData = new FormData();
    formData.append('profilePicture', file);

    const response = await api.put<ProfilePictureResponse>(
      '/agency/profile-picture',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  /**
   * Delete profile picture
   */
  async deleteProfilePicture(): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(
      '/agency/profile-picture'
    );
    return response.data;
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Invalid file type. Only JPEG, PNG, and WebP images are allowed.',
      };
    }

    // Check file size (3MB)
    const maxSize = 3 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'File size exceeds 3MB limit. Please choose a smaller image.',
      };
    }

    return { valid: true };
  }
}

export const profilePictureService = new ProfilePictureService();
export default profilePictureService;
